import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import AgentAvatar from '../AgentAvatar';
import CommonStatusPill from '../../common/StatusPill';

const AVATAR_SIZE = 28;
const AVATAR_RADIUS = '6px';
const TEXT_INDENT = '38px';

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== 'string') return '';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.md')) return 'text/markdown';
  if (lower.endsWith('.txt')) return 'text/plain';
  if (lower.endsWith('.json')) return 'application/json';
  if (lower.endsWith('.csv')) return 'text/csv';
  return '';
}

function formatBytes(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return 'Unknown size';
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    units.length - 1,
    Math.floor(Math.log(size) / Math.log(1024))
  );
  const normalized = size / Math.pow(1024, unitIndex);
  return `${normalized.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getAgentAvatarClickHandler(agentId, onOpenAgentSettings) {
  if (!agentId || typeof onOpenAgentSettings !== 'function') return undefined;
  return (event) => {
    event.stopPropagation();
    onOpenAgentSettings(agentId);
  };
}

function getClickableAgentAvatarSx(avatarClick) {
  if (!avatarClick) return {};
  return {
    cursor: 'pointer',
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(24, 24, 27, 0.2)',
      filter: 'brightness(0.98)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 1px 4px rgba(24, 24, 27, 0.16)',
    },
  };
}

function prettifyToolName(toolName) {
  if (!toolName) return 'create_file';
  return String(toolName)
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();
}

function getFileExtension(fileName) {
  if (typeof fileName !== 'string' || !fileName.includes('.')) return 'FILE';
  return fileName.split('.').pop().toUpperCase();
}

export default function CreatedFileCard({
  toolName,
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenFile = null,
  onOpenAgentSettings = null,
}) {
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const fileId =
    parsedResult?.fileId || parsedResult?.id || parsedArgs?.fileId || null;
  const fileName = parsedResult?.fileName || parsedArgs?.fileName || 'Untitled file';
  const mimeType =
    parsedResult?.mimeType ||
    parsedArgs?.mimeType ||
    inferMimeTypeFromFileName(fileName) ||
    'unknown';
  const downloadUrl = parsedResult?.downloadUrl || null;
  const size = parsedResult?.size ?? parsedResult?.fileSize ?? null;
  const createdAt = parsedResult?.createdAt || parsedResult?.createDate || null;
  const fileAgentId = parsedResult?.agentId || parsedArgs?.agentId || agentId || null;
  const sourceTool = parsedResult?.sourceTool || toolName || null;
  const toolError = parsedResult?.error || null;
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const canOpen = Boolean((fileId || downloadUrl) && typeof onOpenFile === 'function');

  const handleOpen = () => {
    if (!canOpen) return;
    onOpenFile({
      fileId,
      fileName,
      mimeType,
      downloadUrl,
      size,
      createdAt,
      agentId: fileAgentId,
      sourceTool,
      args,
      result,
    });
  };

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box sx={{ pl: grouped ? TEXT_INDENT : 0, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box sx={{ width: '75%', minWidth: '280px' }}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '10px',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: 'background.subtle',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
                  File Created
                </Typography>
              </Box>
              <CommonStatusPill variant="neutral" size="sm" uppercase>
                {getFileExtension(fileName)}
              </CommonStatusPill>
            </Box>

            <Box sx={{ p: 1.5 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.35,
                  mb: 0.5,
                  wordBreak: 'break-word',
                }}
              >
                {fileName}
              </Typography>

              <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 1 }}>
                {toolError
                  ? 'File creation failed. Open details for diagnostics.'
                  : `Created via ${prettifyToolName(sourceTool || toolName)}`}
              </Typography>

              {toolError && (
                <Typography sx={{ fontSize: '12px', color: 'error.main', fontWeight: 600, mb: 1 }}>
                  {toolError}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  Type: <Box component="span" sx={{ fontFamily: 'monospace' }}>{mimeType}</Box>
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  Size: <Box component="span" sx={{ fontFamily: 'monospace' }}>{formatBytes(size)}</Box>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canOpen}
                  onClick={handleOpen}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Open editor
                </Button>
                {downloadUrl && (
                  <Button
                    size="small"
                    variant="text"
                    component="a"
                    href={downloadUrl}
                    download={fileName}
                    startIcon={<DownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: 'none', fontSize: '12px' }}
                  >
                    Download
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
